import React, { useEffect, useCallback, useState } from 'react';
import { ToastAnt } from '../../../Shared/Toast/ToastAnt';

type ContextMenuConfig = {
  show: boolean;
  left: number;
  top: number;
  names: string[];
};

function useContextMenu(canvasEl: Nullable<fabric.Canvas>, hideImages: boolean, onImagesHidden: (objects: fabric.Object[]) => void) {
  const [contextMenuConfig, setContextMenuConfig] = useState<ContextMenuConfig>({ show: false, left: 0, top: 0, names: [] });

  const onClick = useCallback(() => {
    setContextMenuConfig({ ...contextMenuConfig, show: false });
  }, [contextMenuConfig]);

  const onRightClick = useCallback(
    (e: MouseEvent) => {
      if (canvasEl!.getActiveObject()) {
        e.preventDefault();
        let config: ContextMenuConfig = {
          show: true,
          left: e.pageX,
          top: e.pageY,
          names: canvasEl!.getActiveObjects().map(x => x.data.name)
        };
        setContextMenuConfig(config);
      }
    },
    [canvasEl]
  );

  const onHideImages = useCallback(() => {
    const { names } = contextMenuConfig;

    let hiddenObjects: fabric.Object[] = [];

    if (canvasEl && names.length) {
      const objects = canvasEl.getObjects();

      if (objects.filter(x => x.visible).length === names.length) {
        ToastAnt('You cannot hide all images.');
        return;
      }

      hiddenObjects = objects.filter(x => !!x.name && names.includes(x.name));

      if (hiddenObjects) {
        hiddenObjects.forEach(x => {
          x.visible = false;
          canvasEl!.bringToFront(x);
        });
      }

      canvasEl.discardActiveObject();
    }
    onImagesHidden(hiddenObjects);
  }, [canvasEl, contextMenuConfig, onImagesHidden]);

  useEffect(() => {
    if (hideImages) {
      onHideImages();
    }
  }, [hideImages, onHideImages]);

  useEffect(() => {
    window.addEventListener('contextmenu', onRightClick);
    window.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('contextmenu', onRightClick);
      window.removeEventListener('click', onClick);
    };
  }, [onClick, onRightClick]);

  return [contextMenuConfig];
}

export default useContextMenu;
